import {SpineAnimation} from '@fantasmagames/core/src/display/SpineAnimation';
import {ResourcesLoader} from '@fantasmagames/core/src/loader/ResourcesLoader';
import {Easing} from '@fantasmagames/core/src/tween/Easing';
import {View} from '@fantasmagames/slot/src/common/view/View';
import {WinLine} from '@fantasmagames/slot/src/logic/WinLine';
import {GameSymbol} from '@fantasmagames/slot/src/modules/reels/data/GameSymbol';
import {ObjectSize, Position, ReelConstants, SymbolPosition} from '@fantasmagames/slot/src/modules/reels/ReelConstants';
import {ReelsUtils} from '@fantasmagames/slot/src/modules/reels/ReelsUtils';
import {ReelSymbol} from '@fantasmagames/slot/src/modules/reels/spin/ReelSymbol';
import {WinMeter} from '@fantasmagames/slot/src/modules/winpresentation/WinMeter';
import {WinNumbers} from '@fantasmagames/slot/src/modules/winpresentation/WinNumbers';
import {Tween} from '@fantasmagames/core/src/tween/Tween';
import {Updatable} from '@fantasmagames/core/src/update/Updatable';
import {GlowFilter} from '@fantasmagames/slot/src/display/filters/GlowFilter';
import Container = PIXI.Container;
import {Timer} from '@fantasmagames/core/src/update/Timer';

export class WinPresentationView extends View {
	public static readonly COUNTUP_ANIMATION_FINISHED: string = 'WinPresentationView::COUNTUP_ANIMATION_FINISHED';
	public static readonly COUNTUP_ANIMATION_STARTED: string = 'WinPresentationView::COUNTUP_ANIMATION_STARTED';
	public static readonly WIN_NUMBER_SHOWN_TO_PLAYER: string = 'WinPresentationView::WIN_NUMBER_SHOWN_TO_PLAYER';
	public static readonly WIN_NUMBER_BOUNCE_FINISHED: string = 'WinPresentationView::NUMBER_BOUNCE_FINISHED';

	public static readonly WIN_ANIMATION_PLAYING_FOR_SYMBOL: string = 'WinPresentationView::WIN_ANIMATION_PLAYING_FOR_SYMBOL';
	// win indication + extra time to check it
	public static readonly WIN_ANIMATION_FINISHED: string = 'WinPresentationView::WIN_ANIMATION_FINISHED';
	// win symbols indication animation finished
	public static readonly INITIAL_WIN_ANIMATION_FINISHED: string = 'WinPresentationView::INITIAL_WIN_ANIMATION_FINISHED';

	public static DESKTOP_WINLINE_WIDTH: number = 15;
	public static MOBILE_WINLINE_WIDTH: number = 10;

	public static FREESPIN_WINANIMATION_FINISHED_DELAY: number = 3000;
	public static BASEGAME_WINANIMATION_FINISHED_DELAY: number = 500;

	protected _winPresentationAnimations: SpineAnimation[] = [];

	protected _winSymbols: ReelSymbol[][] = [];

	protected _meter: WinMeter;
	protected _winNumber: WinNumbers;
	protected _biggerWinNumber: WinNumbers;

	protected _currentWinNumbers: WinNumbers;

	protected _currentLevel: number;

	protected _currentWinNumberAnimation: SpineAnimation;
	protected _isEndingAnimationInProgress: boolean;

	protected _winTextContainer: Container;
	protected _winSymbolsContainer: Container;
	protected _winLinesContainer: PIXI.Container;

	protected _isPlayingEndAnimation: boolean = false;

	protected _currentSymbolsAnimations: Updatable[] = [];

	protected _winLineBlur1: PIXI.filters.BlurFilter;
	protected _winLineBlur2: PIXI.filters.BlurFilter;

	protected _delayPlayWinEndTimer: Timer;

	protected _currentFinalNumber: number;

	public init(): void {
		super.init();

		this.setupCurrencyFormatter();
		this.initContainers();
		this.createWinSymbols();
		this.createWinTextContainer();

		this._winLineBlur1 = new PIXI.filters.BlurFilter();
		this._winLineBlur1.blur = 16;

		this._winLineBlur2 = new PIXI.filters.BlurFilter();
		this._winLineBlur2.blur = 4;
	}

	public playWinPresentationFor(
		isInitial: boolean,
		winLines: WinLine[],
		reels: GameSymbol[][],
		reelsToSkip?: number[],
		addLine?: boolean,
		PAYLINES?: number[][],
	): void {
		this.killCurrentWinPresentation();
		this.hideWinPresentationSymbols();

		if (addLine && !isInitial) {
			this.tryToAddWinLines(winLines, PAYLINES);
		}

		this.addWinSymbolsIndication(winLines, reels, reelsToSkip);
	}

	public playWinPresentationBeforeBigWin(
		isInitial: boolean,
		winLines: WinLine[],
		reels: GameSymbol[][],
		reelsToSkip?: number[],
		addLine?: boolean,
		PAYLINES?: number[][],
	): void {
		this.killCurrentWinPresentation();
		this.hideWinPresentationSymbols();

		if (addLine && !isInitial) {
			this.tryToAddWinLines(winLines, PAYLINES);
		}

		this.addWinSymbolsIndicationBeforeBigWin(winLines, reels, reelsToSkip);
	}

	protected tryToAddWinLines(winLines: WinLine[], PAYLINES: number[][]): void {
		if (winLines[0]) {
			const nonWinningPartOfLine: SymbolPosition[] = this.buildNonWinPositionsOfLine(winLines[0].positions, winLines[0].index, PAYLINES);
			if (nonWinningPartOfLine.length > 0) {
				const lastWinningSymbolIndex: number = winLines[0].positions.length - 1;
				const lineWidth: number = this.device.isMobile
					? WinPresentationView.MOBILE_WINLINE_WIDTH
					: WinPresentationView.DESKTOP_WINLINE_WIDTH;

				const winLine: PIXI.Container = this.buildWinLine(
					winLines[0].positions[lastWinningSymbolIndex],
					nonWinningPartOfLine,
					lineWidth,
				);
				this._winLinesContainer.addChild(winLine);
			}
		}
	}

	protected addWinSymbolsIndication(winLines: WinLine[], reels: GameSymbol[][], reelsToSkip?: number[]): void {
		for (const line of winLines) {
			for (const pos of line.positions) {
				if (!reelsToSkip || reelsToSkip.indexOf(pos.x) === -1) {
					const sym: ReelSymbol = this.createAndAddWinSymbol(pos, reels);
					const wpFilter: GlowFilter = new GlowFilter(20, 6, 2, 0xffff00);
					sym.filters = [wpFilter];

					this._winSymbolsContainer.addChild(sym);
					this.emit(WinPresentationView.WIN_ANIMATION_PLAYING_FOR_SYMBOL, pos);
				}
			}
		}
		this.onInitialWinAnimationFinished();
	}

	protected addWinSymbolsIndicationBeforeBigWin(winLines: WinLine[], reels: GameSymbol[][], reelsToSkip?: number[]): void {
		for (const line of winLines) {
			for (const pos of line.positions) {
				if (!reelsToSkip || reelsToSkip.indexOf(pos.x) === -1) {
					const sym: ReelSymbol = this.createAndAddWinSymbol(pos, reels);
					const wpFilter: GlowFilter = new GlowFilter(20, 6, 2, 0xffff00);
					sym.filters = [wpFilter];

					this._winSymbolsContainer.addChild(sym);
					this.emit(WinPresentationView.WIN_ANIMATION_PLAYING_FOR_SYMBOL, pos);
				}
			}
		}

		this.onInitialWinAnimationFinishedBeforeBigWin();
	}

	protected buildNonWinPositionsOfLine(winSymbols: SymbolPosition[], lineIndex: number, PAYLINES: number[][]): SymbolPosition[] {
		const nonWinningPositions: SymbolPosition[] = [];

		for (let i = winSymbols.length; i < ReelConstants.REELS_AMOUNT; i++) {
			// TODO: Replace this.
			const rowPos: number = PAYLINES[lineIndex][i];
			nonWinningPositions.push({x: i, y: rowPos});
		}

		return nonWinningPositions;
	}

	public killCurrentWinPresentation(): void {
		this.killAllSpineAnimations();
		this.killWinLines();
		this.hideWinPresentationSymbols();
		this.killWinSymbols();
		if (this._meter) {
			this._meter.stop();
			this._meter = null;
		}

		for (const anim of this._winPresentationAnimations) {
			this.spineUtils.stopAndDestroyAnimation(anim);
		}
		this._winPresentationAnimations = [];
	}

	public hideWinPresentationSymbols(): void {
		for (let reelIndex = 0; reelIndex < ReelConstants.REELS_AMOUNT; reelIndex++) {
			for (let rowIndex = 0; rowIndex < ReelConstants.MAX_ROW_AMOUNT; rowIndex++) {
				this._winSymbols[reelIndex][rowIndex].visible = false;
				this._winSymbols[reelIndex][rowIndex].alpha = 1;
			}
		}
	}

	public playWinNumberCounter(finalNumber: number, isAboveNormalWin: boolean, level: number, isIndividualWin: boolean): void {
		this._currentFinalNumber = finalNumber;
		this.forceKillWinNumbers();

		this._isPlayingEndAnimation = false;
		if (isAboveNormalWin && level === 3) {
			this._currentWinNumbers = this._biggerWinNumber;
		} else {
			this._currentWinNumbers = this._winNumber;
		}

		this.initMeter();

		this._currentLevel = level;

		if (isAboveNormalWin) {
			this._meter.resolutionPadding = 100;
			this._meter.resolutionWidth = this.resolutionManager.resolution.width - 50;
			this._meter.from(0).to(finalNumber, this.getCountingTime());
		} else {
			this._meter.from(finalNumber).to(finalNumber, this.getCountingTime());
			this._meter.resolutionWidth = this.resolutionManager.resolution.width - 50;
		}

		this._meter.start();
		this.updateManager.add(this._meter);

		this.killCurrentAnimation();

		this._isEndingAnimationInProgress = false;
		const animPosition: Position = this.getAnimationPosition();

		this._currentWinNumberAnimation = this.spineUtils
			.startSpineAnimationCreation('WIN_NUMBERS.json', 'START', false)
			.position(animPosition)
			.chainIdleAnimation('IDLE')
			.onComplete(() => {
				this.emitWinNumberBounceFinished();
				this.scaleNumbersDown();

				if (!isAboveNormalWin && !isIndividualWin) {
					this.emitWinNumberShownToPlayer();
				}
			})
			.createAndStart();

		this.spineUtils.replaceElementInSpineAnimation(this._currentWinNumberAnimation, 'NUMBER', this._currentWinNumbers);

		const zeroNumber = this.currencyFormatter.formatCurrency(0);

		this._currentWinNumbers.showNumber(zeroNumber);
		this._currentWinNumbers.visible = true;
	}

	protected getAnimationPosition(): SymbolPosition {
		return {x: ReelConstants.getReelsCenter().x, y: ReelConstants.getReelsCenter().y - ReelConstants.getSymbolSlot().height / 2};
	}

	public skipWin(finalNumber: number, isAboveNormalWin: boolean): void {
		if (this._isPlayingEndAnimation) {
			return;
		}

		if (!this._meter) {
			this._isPlayingEndAnimation = false;

			if (isAboveNormalWin && this._currentLevel === 3) {
				this._currentWinNumbers = this._biggerWinNumber;
			} else {
				this._currentWinNumbers = this._winNumber;
			}

			this.initMeter();

			this._meter.start();
			this.updateManager.add(this._meter);
			this._meter.from(finalNumber).to(finalNumber, this.getCountingTime());

			this._currentWinNumbers.visible = true;
		}

		this.playWinEndAnimation();
	}

	public playWinEndAnimation(): void {
		this.stopDelayWinEndTimer();

		if (this._isPlayingEndAnimation) {
			return;
		}
		this._isPlayingEndAnimation = true;

		if (!this._isEndingAnimationInProgress) {
			if (!this._meter) {
				this.initMeter();
				this._meter.from(this._currentFinalNumber).to(this._currentFinalNumber, this.getCountingTime());
			}

			this.killCurrentAnimation();
			this._meter.snapToFinalValue();

			this._isEndingAnimationInProgress = true;
			const animPosition: Position = this.getAnimationPosition();

			this._currentWinNumberAnimation = this.spineUtils
				.startSpineAnimationCreation('WIN_NUMBERS.json', 'EXIT', false)
				.position(animPosition)
				.onComplete(() => {
					this._isEndingAnimationInProgress = false;
					this.emit(WinPresentationView.WIN_ANIMATION_FINISHED);
				})
				.createAndStart();
			this.spineUtils.replaceElementInSpineAnimation(this._currentWinNumberAnimation, 'NUMBER', this._currentWinNumbers);

			this.endAllWinIndications();

			this._meter.forceUpdate();

			this.emit(WinPresentationView.COUNTUP_ANIMATION_FINISHED);
		}
	}

	protected endAllWinIndications(): void {
		for (const anim of this._winPresentationAnimations) {
			if (anim.isPlaying) {
				if (anim.hasAnimationWithName('EXIT')) {
					anim.changeAnimationTo('EXIT', false);
				}
				anim.unChainIdleAnimation();
			}
		}
	}

	protected stopDelayWinEndTimer(): void {
		if (this._delayPlayWinEndTimer && this._delayPlayWinEndTimer.isPlaying) {
			this._delayPlayWinEndTimer.stop();
			this._delayPlayWinEndTimer = null;
		}
	}

	public forceKillWinNumbers(): void {
		if (this._currentWinNumbers) {
			this._currentWinNumbers.visible = false;
		}
	}

	public setupCurrencyFormatter(): void {
		if (this._winNumber && this._biggerWinNumber) {
			this._winNumber.currencyISO = this._biggerWinNumber.currencyISO = this.currencyFormatter.currency;
			this._winNumber.currencySign = this._biggerWinNumber.currencySign = this.currencyFormatter.currencySign;
		}
	}

	public set winTextContainer(value: PIXI.Container) {
		this._winTextContainer = value;

		const winnumberResource = this.getWinnumberResource(this.device.isMobile, false);

		this._winNumber = new WinNumbers(this.resources, winnumberResource);
		this._winNumber.numberAnchor = {x: 0, y: 0.5};

		this._winTextContainer.addChild(this._winNumber);
		this._winNumber.visible = false;

		const biggerWinNumberResource = this.getWinnumberResource(this.device.isMobile, true);

		this._biggerWinNumber = new WinNumbers(this.resources, biggerWinNumberResource);
		this._biggerWinNumber.numberAnchor = {x: 0, y: 0.5};

		this._winTextContainer.addChild(this._biggerWinNumber);
		this._biggerWinNumber.visible = false;

		this.setupWinNumbersSettings();
	}

	protected getWinnumberResource(isMobile: boolean, isBigger: boolean): string {
		if (isBigger) {
			return isMobile ? 'MED_' : 'MED_';
		}

		return isMobile ? 'SMALL_' : 'MED_';
	}

	protected initContainers(): void {
		this._winLinesContainer = new PIXI.Container();
		this._winSymbolsContainer = new PIXI.Container();

		this.container.addChild(this._winLinesContainer);
		this.container.addChild(this._winSymbolsContainer);
	}

	protected buildWinLine(prevSymbol: SymbolPosition, positions: SymbolPosition[], lineWidth: number = 15): PIXI.Container {
		const container: PIXI.Container = new PIXI.Container();

		const startPos: Position = ReelConstants.getSymbolPosition(prevSymbol);

		startPos.x += ReelConstants.getSymbolSlot().width / 2;

		if (prevSymbol.y > positions[0].y) {
			startPos.y -= ReelConstants.getSymbolSlot().height / 2;
		}

		if (prevSymbol.y < positions[0].y) {
			startPos.y += ReelConstants.getSymbolSlot().height / 2;
		}

		const endPos: Position = ReelConstants.getSymbolPosition(positions[positions.length - 1]);
		endPos.x += ReelConstants.getSymbolSlot().width / 2;

		const line1 = this.makeGraphicsLine(startPos, endPos, positions, lineWidth * 1.5, 0xfbc30b, 1);
		const line3 = this.makeGraphicsLine(startPos, endPos, positions, lineWidth, 0xffffff);

		const line1Container: PIXI.Container = new PIXI.Container();
		const line3Container: PIXI.Container = new PIXI.Container();

		container.addChild(line1Container);
		container.addChild(line3Container);

		line1Container.addChild(line1);
		line3Container.addChild(line3);

		if (this.device.desktop) {
			line1Container.filters = [this._winLineBlur1];

			const line2Container: PIXI.Container = new PIXI.Container();

			container.addChildAt(line2Container, 1);

			const line2 = this.makeGraphicsLine(startPos, endPos, positions, lineWidth * 1.2, 0xfbc30b, 1);
			line2Container.addChild(line2);
			line2Container.filters = [this._winLineBlur1];
		}

		return container;
	}

	protected makeGraphicsLine(
		startPos: SymbolPosition,
		endPos: SymbolPosition,
		positions: SymbolPosition[],
		lineWidth: number = 15,
		lineColor: number = 0xaa0000,
		lineAlpha: number = 1,
	): PIXI.Graphics {
		const line: PIXI.Graphics = new PIXI.Graphics();

		line.lineStyle(lineWidth, lineColor, lineAlpha);
		line.moveTo(startPos.x, startPos.y);

		for (const pos of positions) {
			const symPos: Position = ReelConstants.getSymbolPosition(pos);
			line.lineTo(symPos.x, symPos.y);
		}

		line.lineTo(endPos.x, endPos.y);

		return line;
	}

	public killWinLines(): void {
		this._winLinesContainer.removeChildren();
	}

	protected killWinSymbols(): void {
		this._winSymbolsContainer.removeChildren();
	}

	protected addAllSymbolsIntoSprite(spr: PIXI.Sprite, resources: ResourcesLoader): void {
		throw new Error('WinPresentationView::addAllSymbolsIntoSprite must be overriden');
	}

	protected addBackgroundsIntoSprite(spr: PIXI.Sprite, resources: ResourcesLoader): void {
		throw new Error('WinPresentationView::addBackgroundsIntoSprite must be overriden');
	}

	protected getSizedSimpleWinAnimationName(isInitial: boolean, size: ObjectSize): string {
		throw new Error('WinPresentationView::getSizedSimpleWinAnimationName must be overriden');
	}

	protected addForegroundIntoSprite(spr: PIXI.Sprite): void {
		throw new Error('WinPresentationView::addForegroundIntoSprite must be overriden');
	}

	protected makeSizedSymbol(symbol: number, size: ObjectSize = {width: 1, height: 1}): number {
		throw new Error('WinPresentationView::makeSizedSymbol must be overriden');
	}

	protected getWinSymbolPosition(posOnReels: SymbolPosition, reels: GameSymbol[][]): Position {
		const position: Position = ReelsUtils.getPositionOnReels(posOnReels, reels);

		return {
			x: position.x - ReelConstants.getSymbolSlot().width / 2,
			y: position.y - ReelConstants.getSymbolSlot().height / 2,
		};
	}

	protected createWinTextContainer(): void {
		const winTextContainer: PIXI.Container = new PIXI.Container();
		const winTextPosition = this.layout.getPosition('WIN_POSITION');
		winTextContainer.position.set(winTextPosition.x, winTextPosition.y);
		this.container.addChild(winTextContainer);
		this.winTextContainer = winTextContainer;
	}

	protected createWinSymbols(): void {
		for (let reelIndex = 0; reelIndex < ReelConstants.REELS_AMOUNT; reelIndex++) {
			this._winSymbols[reelIndex] = [];
			for (let rowIndex = 0; rowIndex < ReelConstants.MAX_ROW_AMOUNT; rowIndex++) {
				const symbolsSprite: PIXI.Sprite = new PIXI.Sprite();
				this.addAllSymbolsIntoSprite(symbolsSprite, this.resources);

				const symbolBackground: PIXI.Sprite = new PIXI.Sprite();
				this.addBackgroundsIntoSprite(symbolBackground, this.resources);

				const symbolForeground: PIXI.Sprite = new PIXI.Sprite();
				this.addForegroundIntoSprite(symbolForeground);

				const symbol: ReelSymbol = this.createReelSymbol();

				symbol.setSpineUtils(this.spineUtils);
				symbol.init(symbolsSprite, symbolBackground, symbolForeground);
				symbol.setSymbol(1);

				const offsets = {
					x: ReelConstants.getReelsOffsets().x + reelIndex * ReelConstants.getSymbolSlot().width,
					y: ReelConstants.getReelsOffsets().y,
				};

				symbol.x = offsets.x;
				symbol.y = offsets.y + rowIndex * ReelConstants.getSymbolSlot().height;

				symbol.visible = false;

				this._winSymbolsContainer.addChild(symbol);
				this._winSymbols[reelIndex][rowIndex] = symbol;
			}
		}
	}

	protected createAndAddWinSymbol(pos: SymbolPosition, reels: GameSymbol[][]): ReelSymbol {
		const sym: ReelSymbol = this._winSymbols[pos.x][pos.y];
		const symSize: ObjectSize = reels[pos.x][pos.y].size;
		const symPosition: Position = this.getWinSymbolPosition(pos, reels);
		sym.visible = true;
		sym.x = symPosition.x;
		sym.y = symPosition.y;
		sym.setSymbol(this.makeSizedSymbol(reels[pos.x][pos.y].sym, symSize));

		return sym;
	}

	protected killAllSpineAnimations(): void {
		for (const anim of this._currentSymbolsAnimations) {
			if (anim instanceof SpineAnimation) {
				this.spineUtils.stopAndDestroyAnimation(anim);
			}
			if (anim instanceof Tween) {
				anim.stop();
			}
		}
		this._currentSymbolsAnimations = [];
	}

	protected getSumOfHeightsBefore(symPos: SymbolPosition, reels: GameSymbol[][]): number {
		let sum: number = 0;
		for (let i = reels[symPos.x].length - 1; i >= symPos.y; i--) {
			sum += reels[symPos.x][i].size.height;
		}

		return sum;
	}

	protected onInitialWinAnimationFinished(): void {
		this.emitDelayed(WinPresentationView.INITIAL_WIN_ANIMATION_FINISHED);
	}

	protected onInitialWinAnimationFinishedBeforeBigWin(): void {
		this.emitDelayed(WinPresentationView.INITIAL_WIN_ANIMATION_FINISHED);
		this.emitDelayed(WinPresentationView.WIN_ANIMATION_FINISHED);
	}

	protected setupMeter(): void {
		this._meter.resolutionPadding = 100;
		this._meter.resolutionWidth = this.resolutionManager.resolution.width;
	}

	protected getCurrentWinNumbersPosition(): SymbolPosition {
		return {
			x: ReelConstants.getReelsOffsets().x + ReelConstants.getSymbolSlot().width * (ReelConstants.REELS_AMOUNT / 2),
			y:
				ReelConstants.getReelsOffsets().y +
				ReelConstants.getSymbolSlot().height +
				(ReelConstants.ROW_AMOUNT / 2) * ReelConstants.getSymbolSlot().height,
		};
	}

	protected scaleNumbersDown(): void {
		const parent = this._currentWinNumbers.container;

		if (this._currentWinNumbers.totalWidth > this.resolutionManager.resolution.width) {
			const width = this._currentWinNumbers.totalWidth;
			parent.scale.set(this.resolutionManager.resolution.width / width);
		} else {
			parent.scale.set(1);
		}
	}

	protected emitWinNumberBounceFinished(): void {
		this.emit(WinPresentationView.WIN_NUMBER_BOUNCE_FINISHED);
	}

	protected emitWinNumberShownToPlayer(): void {
		this.emit(WinPresentationView.WIN_NUMBER_SHOWN_TO_PLAYER);
	}

	protected initMeter(): void {
		this._meter = new WinMeter(this._currentWinNumbers, Easing.Linear.None, this.currencyFormatter, true);
		this._meter.onCompleteCallback = this.onMeterFinished.bind(this);
	}

	protected killCurrentAnimation(): void {
		this.spineUtils.stopAndDestroyAnimation(this._currentWinNumberAnimation);
	}

	protected onMeterFinished(): void {
		let delay = 0;

		if (this._currentLevel === 2) {
			delay = 300;
		}

		if (this._currentLevel === 3) {
			delay = 600;
		}

		this._delayPlayWinEndTimer = new Timer(
			() => {
				this.playWinEndAnimation();
			},
			delay,
			true,
		);
		this.updateManager.add(this._delayPlayWinEndTimer);
	}

	protected getCountingTime(): number {
		switch (this._currentLevel) {
			case 2:
				return 1000;
			case 3:
				return 2300;
			default:
				return 2000;
		}
	}

	protected createReelSymbol(): ReelSymbol {
		return new ReelSymbol();
	}

	protected setupWinNumbersSettings(): void {
		this._winNumber.currencyISO = this.currencyFormatter.currency;
		this._winNumber.currencySign = this.currencyFormatter.currencySign;
		this._biggerWinNumber.currencyISO = this.currencyFormatter.currency;
		this._biggerWinNumber.currencySign = this.currencyFormatter.currencySign;
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/slot/src/modules/winpresentation/WinPresentationView.ts